//* Import API configurations
import { api } from "../../services/apis/configs/axiosConfigs";

//* Import API URI constant
import { API_URI } from "../../constants/consts";

//* Import PaginationData type
import { type Pagination } from "../../services/stores/types";
import { CSVStudentValidator } from "../../utils/csv/csv-validatorStudent";

//* Import types from types
import {
  Student,
  StudentImportResult,
  StudentsDetails,
  StudentResponse,
} from "../students/types/types";

// API methods for Student operations
export const studentAPI = {
  // Fetch all Students with optional search and pagination
  getAll: async function (
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10
  ): Promise<StudentResponse> {
    const searchQuery = search ? `&search=${search}` : "";
    const url = `${API_URI}/admin/private/students?page=${page}&perPage=${perPage}${searchQuery}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          students: data.students || [],
          pagination,
        };
      })
      .catch((error) => {
        console.error("Error fetching students:", error);
        throw new Error("Failed to fetch students");
      });
  },

  // Fetch a Student by ID
  getById: async function (
    id: string,
    token: string
  ): Promise<StudentsDetails> {
    return api
      .request({
        url: `${API_URI}/admin/private/students/${id}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        return response.data.data;
      })
      .catch((error) => {
        console.error("Error fetching Student:", error);
        throw new Error("Failed to fetch Student");
      });
  },

  // Create a new collaborators
  create: async function (
    data: { name: string; email: string },
    token: string
  ): Promise<Student> {
    try {
      const response = await api.request({
        url: `${API_URI}/admin/private/students`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data,
      });
      return response.data.data;
    } catch (error) {
      console.error("Error creating admin:", error, data);
      throw error;
    }
  },

  createBatch: async function (
    students: CSVStudentValidator[],
    token: string
  ): Promise<StudentImportResult> {
    return api
      .request({
        url: `${API_URI}/admin/private/students/batch`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: { students },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        console.error("Error processing CSVStudent:", error);
        throw error;
      });
  },

  // Update an existing Student
  update: async function (
    id: string,
    data: { name: string; email: string; reset_password?: boolean },
    token: string
  ): Promise<Student> {
    return api
      .request({
        url: `${API_URI}/admin/private/students/${id}`,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error updating Student:", error);
        throw new Error("Failed to update Student");
      });
  },

  // Delete an Student by ID
  delete: async function (id: string, token: string): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/students/${id}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error deleting Student:", error);
        throw new Error("Failed to delete Student");
      });
  },

  // Restore an Student by ID
  restoreByBatch: async function (id: string, token: string): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/students/restore/batch/${id}`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error restoring Student:", error);
        throw new Error("Failed to restore Student");
      });
  },

  restoreByEmail: async function (email: string, token: string): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/students/restore/email/`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: { email },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error restoring Student:", error);
        throw new Error("Failed to restore Student");
      });
  }
};
